import React, { useMemo, useState, useCallback, useEffect, useRef } from 'react';
import axios from 'axios';
import Modal from 'react-modal';
import './modal.css';
import { useMonitoramento } from '../../context/MonitoramentoContext';
import { isCertificadoPortal } from '../../utils/certificado';
import { parseServerDate } from '../../utils/serverDate';

const axiosInstance = axios.create({
  baseURL: `${process.env.REACT_APP_API_823 || ''}/pdv_exception`,
  auth: { username: 'Portal', password: 'Portal1163' },
});

function formatarData(dataInput) {
  return parseServerDate(dataInput).toLocaleString('pt-BR');
}

function ultimaOcorrencia(records) {
  return records.reduce((max, r) => {
    const d = parseServerDate(r.recepcao);
    return d > max ? d : max;
  }, new Date(0));
}

// Total de ocorrências de Certificado do Portal, para uso no botão da aba.
export function useCertificadoCount() {
  const { pdvData } = useMonitoramento();
  return useMemo(() =>
    (pdvData || []).filter(r => r.cnpj !== '11111111111111' && isCertificadoPortal(r)).length,
    [pdvData]
  );
}

export function CertificadoBadge() {
  const count = useCertificadoCount();
  const [bump, setBump] = useState(false);
  const prevCountRef = useRef(count);

  useEffect(() => {
    if (count > prevCountRef.current) {
      setBump(true);
      const t = setTimeout(() => setBump(false), 500);
      prevCountRef.current = count;
      return () => clearTimeout(t);
    }
    prevCountRef.current = count;
  }, [count]);

  if (!count) return null;
  return (
    <span className={`badge bg-warning text-dark rounded-pill ms-2${bump ? ' cs-badge-bump' : ''}`}>
      {count}
    </span>
  );
}

const LogCertificado = () => {
  const { pdvData, refresh } = useMonitoramento();
  const [busca, setBusca] = useState('');
  const [lidos, setLidos] = useState(() => new Set());
  const [modalData, setModalData] = useState(null); // { cnpj, nome, itens }

  const registros = useMemo(() => {
    const ocorrencias = (pdvData || [])
      .filter(r => r.cnpj !== '11111111111111')
      .filter(r => isCertificadoPortal(r))
      .filter(r => !lidos.has(r.controle));

    const termo = busca.trim().toLowerCase();
    return termo
      ? ocorrencias.filter(r =>
          (r.nome || '').toLowerCase().includes(termo) ||
          (r.cnpj || '').toLowerCase().includes(termo)
        )
      : ocorrencias;
  }, [pdvData, busca, lidos]);

  const grupos = useMemo(() => {
    const map = new Map();
    registros.forEach((r) => {
      if (!map.has(r.cnpj)) {
        map.set(r.cnpj, { cnpj: r.cnpj, nome: r.nome, itens: [] });
      }
      map.get(r.cnpj).itens.push(r);
    });
    return Array.from(map.values())
      .sort((a, b) => ultimaOcorrencia(b.itens) - ultimaOcorrencia(a.itens));
  }, [registros]);

  const marcarComoLido = useCallback(async (id) => {
    setLidos((prev) => new Set(prev).add(id));
    setModalData((prev) => prev && ({
      ...prev,
      itens: prev.itens.filter((r) => r.controle !== id),
    }));
    try {
      await axiosInstance.post(`/${id}`, '');
      refresh();
    } catch (error) {
      console.error('Erro ao marcar como lido:', error);
    }
  }, [refresh]);

  const marcarTodosComoLido = useCallback(async () => {
    const ids = registros.map(r => r.controle);
    if (ids.length === 0) return;
    setLidos((prev) => {
      const novo = new Set(prev);
      ids.forEach(id => novo.add(id));
      return novo;
    });
    try {
      await Promise.allSettled(ids.map(id => axiosInstance.post(`/${id}`, '')));
      refresh();
    } catch (error) {
      console.error('Erro ao marcar todos como lido:', error);
    }
  }, [registros, refresh]);

  const marcarGrupoComoLido = useCallback(async (itens) => {
    const ids = itens.map(r => r.controle);
    setLidos((prev) => {
      const novo = new Set(prev);
      ids.forEach(id => novo.add(id));
      return novo;
    });
    setModalData(null);
    try {
      await Promise.allSettled(ids.map(id => axiosInstance.post(`/${id}`, '')));
      refresh();
    } catch (error) {
      console.error('Erro ao marcar grupo como lido:', error);
    }
  }, [refresh]);

  return (
    <div>
      <div className="d-flex align-items-center gap-2 mb-2 flex-wrap">
        <input
          type="text"
          className="form-control form-control-sm"
          style={{ maxWidth: 280 }}
          placeholder="Filtrar por cliente ou CNPJ..."
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
        />
        {busca && (
          <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setBusca('')}>
            Limpar ×
          </button>
        )}
        <small className="text-muted ms-auto">{registros.length} ocorrência(s) em {grupos.length} cliente(s)</small>
        {registros.length > 0 && (
          <button
            type="button"
            className="btn btn-sm btn-outline-success"
            onClick={marcarTodosComoLido}
          >
            &#10003; Marcar todos como lido
          </button>
        )}
      </div>

      <div className="cs-cont-log">
        {grupos.length > 0 ? (
          grupos.map((grupo) => (
            <div key={grupo.cnpj} className="cs-cont-line cs-sev-warning">
              <span className="cs-cont-time">{formatarData(ultimaOcorrencia(grupo.itens))}</span>
              <span className="cs-cont-cliente">{grupo.nome}</span>
              <span className="cs-cont-msg">
                {grupo.cnpj}
                <span className="badge bg-warning text-dark rounded-pill ms-2">{grupo.itens.length}</span>
              </span>
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary cs-cont-lido"
                title="Ver detalhes desse CNPJ"
                onClick={() => setModalData(grupo)}
              >
                ...
              </button>
            </div>
          ))
        ) : (
          <div className="alert alert-info" role="alert">
            Nenhuma ocorrência de certificado encontrada.
          </div>
        )}
      </div>

      <Modal
        isOpen={!!modalData}
        onRequestClose={() => setModalData(null)}
        contentLabel="Detalhes"
        className="react-modal-content"
        overlayClassName="react-modal-overlay"
      >
        <div className="modal-header modal-header-wrapper">
          <h5 className="modal-title">
            Certificado do Portal
            {modalData?.nome && <small className="text-muted ms-2 fw-normal fs-6">— {modalData.nome}</small>}
          </h5>
          <button type="button" className="btn-close" aria-label="Fechar" onClick={() => setModalData(null)}></button>
        </div>
        {modalData?.itens?.length > 0 ? (
          <div className="modal-table">
            <table className="table table-hover font-sm data-table">
              <thead className="table-header">
                <tr>
                  <th>Data</th>
                  <th>Mensagem</th>
                  <th>Ação</th>
                </tr>
              </thead>
              <tbody>
                {modalData.itens.map((registro) => (
                  <tr key={registro.controle}>
                    <td className="limited-cell text-nowrap">{formatarData(registro.recepcao)}</td>
                    <td style={{ width: '100%' }} dangerouslySetInnerHTML={{ __html: registro.mensagem }} />
                    <td>
                      <button className="btn btn-sm btn-outline-success" onClick={() => marcarComoLido(registro.controle)}>
                        &#10003; Lido
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p>Nenhum registro encontrado.</p>
        )}
        {modalData?.itens?.length > 0 && (
          <div className="modal-footer justify-content-end">
            <button className="btn btn-outline-success" onClick={() => marcarGrupoComoLido(modalData.itens)}>
              &#10003; Marcar todos deste CNPJ como lido
            </button>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default LogCertificado;
